import { action, observable } from 'mobx';
import { ICreateMeetingTemplateDTO, IMeetingTemplateDTO } from '../../../../api/blueprints/blueprints.dao';
import { configList, FieldKey } from './meeting-template-form.config';
import { MeetingExpiryDateVM } from '../../../_events/_manage-event/_meetings-list/_meeting-form/_meeting-expiry-date/meeting-expiry-date.viewmodel';
import { FormattedMessage } from 'react-intl';
import { MeetingTemplateLinksVM } from './meeting-template-links/meeting-template-links.viewmodel';
import { MeetingTemplateMapper, MeetingTemplateValues } from './meeting-template.mapper';
import { Access } from '../../../../api/meetings.dao';
import { BaseBlueprintFormVM } from '../../blueprint-form-base.viewmodel';
import { MeetingTemplateLink } from '../../../../api/blueprints/meeting.template.types';
import { TObject } from '../../../../infrastructure/type-extensions';
import { BLUEPRINTS_TRACKING_EVENTS, convertBoolTypeEmptyForMixPanel, isTrackingFieldEnabled } from '../../../../tracking-service/tracking-values';
import { Space } from '../../../../constants/html-character-entities';
import React from 'react';
import * as IoC from 'typescript-ioc';
import { IAlertsService } from '../../../../infrastructure/alerts';
import { IConnectionTemplateDTO } from '../../../../api/blueprints/connection.template.types';
import { convertBlueprintTemplateIntoBlueprintDetails } from '../../../../infrastructure/utils/blueprint-details.mapper';
import { IBlueprintItemDetails } from '../../../../api/blueprints/blueprint-details.types';
import { ConnectionVersion, NONE } from '../../../../constants/common.constants';

export class MeetingTemplatesFormVM extends BaseBlueprintFormVM {
    @observable
    private meetingTemplateLinksVM: MeetingTemplateLinksVM;
    @observable
    public connectionsTemplatesList: IConnectionTemplateDTO[] = [];
    @observable
    public expiryVM: MeetingExpiryDateVM;

    constructor(
        private readonly accountId: number,
        readonly blueprintId: string,
        private readonly apiCallSuccessCallback: () => void,
        private readonly deleteCompletionCallback: () => void,
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
    ) {
        super(blueprintId);
    }

    public get linksVM(): MeetingTemplateLinksVM {
        return this.meetingTemplateLinksVM;
    }

    public get connectionsTemplates(): IConnectionTemplateDTO[] {
        const templateConectionVersion = this.formDataCtrl.getValue(FieldKey.CONNECTION_VERSION) ? ConnectionVersion.V2 : ConnectionVersion.V1;

        return this.connectionsTemplatesList
            .filter((template: IConnectionTemplateDTO) => {
                const templateVersion = template.connectionVersion ?? ConnectionVersion.V1;
                return templateVersion === templateConectionVersion;
            });
    }

    @action
    public selectMeetingAccess(value: Access | string): void {
        if (!value || value === Access.Closed || value === Access.Bookable) {
            this.formDataCtrl.setValue(FieldKey.REPLAY, false);
            if (value === Access.Closed || !value) {
                this.disableSingleMeetingCustomPage();
            }
        }
        this.formDataCtrl.setValue(FieldKey.MEETING_ACCESS, value);
    }

    public singleMeetingCustomPageAvailable(): boolean {
        const meetingAccessValue = this.formDataCtrl.getValue(FieldKey.MEETING_ACCESS);
        return meetingAccessValue === Access.Open || meetingAccessValue === Access.Plenary || meetingAccessValue === Access.Bookable;
    }

    @action
    public enableSingleMeetingCustomPage(): void {
        this.formDataCtrl.setValue(FieldKey.SINGLE_MEETING_CUSTOM_PAGE, true);
    }

    @action
    public disableSingleMeetingCustomPage(): void {
        this.formDataCtrl.setValue(FieldKey.SINGLE_MEETING_CUSTOM_PAGE, false);
        this.resetSingleMeetingCustomPageFields();
    }

    @action
    public enableLandingPage(): void {
        this.formDataCtrl.setValue(FieldKey.LANDING_PAGE, true);
    }

    @action
    public disableLandingPage(): void {
        this.formDataCtrl.setValue(FieldKey.LANDING_PAGE, false);
        this.formDataCtrl.setValue(FieldKey.DISABLE_OTHER_PAGES, false);
    }

    @action
    public enableApplyConnectionTemplate(): void {
        this.formDataCtrl.setValue(FieldKey.APPLY_CONNECTIONS_TEMPLATE, true);
    }

    @action
    public disableApplyConnectionTemplate(): void {
        this.formDataCtrl.setValue(FieldKey.APPLY_CONNECTIONS_TEMPLATE, false);
        this.formDataCtrl.setValue(FieldKey.CONNECTIONS_TEMPLATE, '');
    }

    @action
    public toggleConnectionVersion(value: boolean): void {
        this.formDataCtrl.setValue(FieldKey.CONNECTION_VERSION, !value);
        this.formDataCtrl.setValue(FieldKey.CONNECTIONS_TEMPLATE, '');
    }

    @action.bound
    public async createMeetingTemplate(): Promise<boolean> {
        try {
            this.startLoading();

            const dto = MeetingTemplateMapper.toMeetingTemplateDto(this.composeMeetingTemplateValues());
            await this.blueprintsDAO.createBlueprint<ICreateMeetingTemplateDTO, IMeetingTemplateDTO>(dto);

            this.successApiCallHandler();

            return true;
        } catch (error) {
            MeetingTemplatesFormVM.handleApiException(error);
        } finally {
            this.stopLoading();
        }
    }

    @action.bound
    public async editMeetingTemplate(): Promise<boolean> {
        try {
            this.startLoading();
            const additionalValues = this.composeAdditionalTemplateValues();
            const dto = MeetingTemplateMapper.toEditMeetingTemplateDTO(this.composeMeetingTemplateValues(additionalValues));
            await this.blueprintsDAO.editBlueprint<IMeetingTemplateDTO>(dto);

            this.successApiCallHandler();

            return true;
        } catch (error) {
            MeetingTemplatesFormVM.handleApiException(error);
        } finally {
            this.stopLoading();
        }
    }

    public submitForm = (): Promise<boolean> => {
        if (!this.formVM.isVmValid() || this.expiryVM.hasValidationError()) {
            this.formVM.setVmTouched();
        } else {
            return this.callProperSubmitAction();
        }
    };

    @action
    public async initFormVM(): Promise<void> {
        if (this.blueprintId) {
            const result = await this.getBluePrint(this.accountId) as IMeetingTemplateDTO;
            this.fromDTO(result);
        }

        await this.initConnectionsTemplates();

        this.initLinksVM(this.blueprintItemSnapshot.links as MeetingTemplateLink[]);
        this.initializeFormData(configList, this.blueprintItemSnapshot);
        this.initExpiryVM();
    }

    public getSelectedBlueprint(): IConnectionTemplateDTO | null {
        const selectedId = this.formDataCtrl.getValue(FieldKey.CONNECTIONS_TEMPLATE) as string;
        return this.connectionsTemplates.find(x => x.blueprintId === selectedId) ?? null;
    };

    public getSelectedBlueprintDetails = (): IBlueprintItemDetails | null => {
        const selectedBlueprint = this.getSelectedBlueprint();
        return selectedBlueprint
            ? convertBlueprintTemplateIntoBlueprintDetails(selectedBlueprint.type, selectedBlueprint)
            : null;
    };

    @action
    private async initConnectionsTemplates(): Promise<void> {
        try {
            this.connectionsTemplatesList = await this.blueprintsDAO.getConnectionTemplateList({ accountId: this.accountId });
        } catch (error) {
            MeetingTemplatesFormVM.handleApiException(error);
        }
    }

    private successApiCallHandler = (): void => {
        this.trackSubmitBlueprintFormMixPanel();
        this.apiCallSuccessCallback();
    };

    private composeAdditionalTemplateValues = (): TObject<string> => {
        return {
            blueprintId: this.blueprintId,
            createdAt: this.blueprintItemSnapshot.createdAt as string,
        };
    };

    private callProperSubmitAction = (): Promise<boolean> => {
        return this.isEditMode
            ? this.editMeetingTemplate()
            : this.createMeetingTemplate();
    };

    @action
    private fromDTO = (dto: IMeetingTemplateDTO): void => {
        this.blueprintItemSnapshot = MeetingTemplateMapper.toMeetingTemplateValuesFromMeetingTemplateDTO(dto);
    };

    @action
    private initLinksVM(links: MeetingTemplateLink[] = []): void {
        this.meetingTemplateLinksVM = new MeetingTemplateLinksVM(links);
    }

    private composeMeetingTemplateValues(additionalValues: TObject<string> = {}): MeetingTemplateValues {
        const values = { accountId: this.accountId, ...additionalValues } as MeetingTemplateValues;

        this.formDataCtrl.getVmState().forEach(({ name, value }) => {
            values[name] = value;
        });

        values.links = this.linksVM.getLinks();
        values[FieldKey.EXPIRY_DAYS] = this.expiryVM.expiryDays;

        return values;
    }

    @action
    private initExpiryVM(): void {
        const expiryDays = this.blueprintItemSnapshot.expiryDays as number | null;
        this.expiryVM = new MeetingExpiryDateVM(expiryDays);
    }

    private trackSubmitBlueprintFormMixPanel(): void {
        const eventType = this.isEditMode ? BLUEPRINTS_TRACKING_EVENTS.EDIT_MEETING_TEMPLATE : BLUEPRINTS_TRACKING_EVENTS.NEW_MEETING_TEMPLATE;

        this.trackingService.get().track(eventType, {
            'Account ID': this.accountId,
            'Account Name': this.globalStorage.getAccount()?.name,
            'Template Name': this.buildEditableFieldCheckMixPanel(FieldKey.NAME),
            'Template ID': this.blueprintId,
            'Template Description': convertBoolTypeEmptyForMixPanel(!!this.formDataCtrl.getValue(FieldKey.DESCRIPTION)),
            'Meeting Status': this.buildEditableFieldCheckMixPanel(FieldKey.MEETING_STATUS),
            'Meeting Access': this.buildEditableFieldCheckMixPanel(FieldKey.MEETING_ACCESS),
            'Record Meeting Toggle': isTrackingFieldEnabled(!!this.formDataCtrl.getValue(FieldKey.RECORD)),
            'Replays Page Toggle': isTrackingFieldEnabled(!!FieldKey.REPLAY),
            'Featured Presentation Toggle': isTrackingFieldEnabled(!!FieldKey.FEATURED),
            'Type': this.buildEditableFieldCheckMixPanel(FieldKey.TYPE),
            'Track': this.buildEditableFieldCheckMixPanel(FieldKey.TRACK),
            'Topic': this.buildEditableFieldCheckMixPanel(FieldKey.TOPIC),
            'Links': this.linksVM.getLinks().length || '0',
            'Meeting Notes': convertBoolTypeEmptyForMixPanel(!!this.formDataCtrl.getValue(FieldKey.NOTES)),
            'Create Single Meeting Custom Page': isTrackingFieldEnabled(!!FieldKey.SINGLE_MEETING_CUSTOM_PAGE),
            'Page Label': this.buildEditableFieldCheckMixPanel(FieldKey.PAGE_LABEL),
            'Page Path': this.buildEditableFieldCheckMixPanel(FieldKey.PAGE_PATH),
            'Show link on Welcome page': isTrackingFieldEnabled(!!FieldKey.PAGE_LINK_ENABLED),
            'Description': convertBoolTypeEmptyForMixPanel(!!this.formDataCtrl.getValue(FieldKey.LINK_DESCRIPTION)),
            'Assigned as Landing Page': isTrackingFieldEnabled(!!FieldKey.LANDING_PAGE),
            'Disable all other pages': isTrackingFieldEnabled(!!FieldKey.DISABLE_OTHER_PAGES),
            'Connection Template': this.getSelectedBlueprint()?.name || NONE,
            'V2 Connections': isTrackingFieldEnabled(!!this.formDataCtrl.getValue(FieldKey.CONNECTION_VERSION)),
            'Meeting expiration days': this.expiryVM.expiryDays ?? NONE
        });
    }

    @action
    private resetSingleMeetingCustomPageFields(): void {
        this.formDataCtrl.setValue(FieldKey.PAGE_PATH, '');
        this.formDataCtrl.setValue(FieldKey.PAGE_LABEL, '');
        this.formDataCtrl.setValue(FieldKey.PAGE_LINK_ENABLED, false);
        this.formDataCtrl.setValue(FieldKey.LINK_DESCRIPTION, '');
        this.formDataCtrl.setValue(FieldKey.LANDING_PAGE, false);
        this.formDataCtrl.setValue(FieldKey.DISABLE_OTHER_PAGES, false);
    }

    public deleteMeetingTemplate = (): void => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage
                        id='oe.confirm.areYouSureTo'
                        description='Confirm message'
                        defaultMessage='Are you sure you want to'
                    />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage
                            id='oe.meetingTemplate.deleteTemplate'
                            description='Confirm message'
                            defaultMessage='Delete this Template?'
                        />
                    </span>
                    <Space />
                    <FormattedMessage
                        id='oe.meetingTemplate.thisCannotBeUndoneAllMeetings'
                        description='Alert message'
                        defaultMessage='This cannot be undone.  All Meetings previously created using this
                 Template will lose their association with it, and no further automations will take place.'
                    />
                </span>
            ),
            confirmButtonProps: {
                text: <FormattedMessage
                    description='Label text'
                    defaultMessage='Yes, Delete'
                    id='oe.meetingTemplate.YesDelete'
                />,
                modifier: 'confirm-warn',
                handler: this.deleteMeetingTemplateApiCall,
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    public deleteMeetingTemplateApiCall = async (): Promise<void> => {
        try {
            await this.blueprintsDAO.deleteBlueprint({
                accountId: this.accountId,
                blueprintId: this.blueprintId
            });

            this.trackDeleteMeetingTemplateMixPanel();
            this.deleteCompletionCallback();
        } catch (error) {
            MeetingTemplatesFormVM.handleApiException(error);
        }
    };

    public trackDeleteMeetingTemplateMixPanel = (): void => {
        this.trackingService.get().track(BLUEPRINTS_TRACKING_EVENTS.DELETE_MEETING_TEMPLATE, {
            'Account Name': this.globalStorage.getAccount()?.name,
            'Account ID': this.accountId,
            'Template Name': this.formDataCtrl.getValue(FieldKey.NAME),
            'Template ID': this.blueprintId,
        });
    };

}
